import { useEffect, useMemo, useState } from 'react';

import { Loader2, X } from 'lucide-react';
import { getCommuneLabel } from 'shared/config/countries';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useUpdateSubProjectMutation } from '../services/projects.api';
import { SubProjectDetail, UpdateSubProjectBody } from './project-detail.types';

interface Props {
  project: SubProjectDetail;
}

interface Draft {
  name: string;
  commune: string;
  externalId: string;
}

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const { data } = error as { data: unknown };
    if (typeof data === 'string') return data;
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const message = (data as { message: unknown }).message;
      if (typeof message === 'string') return message;
    }
  }
  return fallback;
};

const buildDraft = (project: SubProjectDetail): Draft => ({
  name: project.name ?? '',
  commune: project.commune ?? '',
  externalId: project.externalId ?? '',
});

const CONFLICT_STATUS = 409;

/**
 * The API answers 409 when another subproject of the same master already holds the externalId.
 * Koa serves that message as text/plain, so fetchBaseQuery's JSON parse fails and the real code
 * arrives as `originalStatus` behind a PARSING_ERROR — the same shape `isUnauthorizedError`
 * unwraps for 401 in shared/api/baseApi.ts. Both forms are accepted here.
 */
const isExternalIdConflict = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const { status, originalStatus } = error as { status?: unknown; originalStatus?: unknown };
  return status === CONFLICT_STATUS || (status === 'PARSING_ERROR' && originalStatus === CONFLICT_STATUS);
};

const SubProjectIdentityCard = ({ project }: Props) => {
  const [updateSubProject, { isLoading }] = useUpdateSubProjectMutation();
  const [draft, setDraft] = useState<Draft>(() => buildDraft(project));
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPatch, setPendingPatch] = useState<UpdateSubProjectBody | null>(null);
  const [isCreatingExternalId, setIsCreatingExternalId] = useState(false);
  const communeLabel = getCommuneLabel(project.countryCode);

  useEffect(() => {
    setDraft(buildDraft(project));
    setIsCreatingExternalId(false);
  }, [project]);

  const hasExternalId = Boolean(project.externalId);
  const showExternalIdInput = hasExternalId || isCreatingExternalId;

  const cancelCreateExternalId = () => {
    setIsCreatingExternalId(false);
    setDraft((prev) => ({ ...prev, externalId: project.externalId ?? '' }));
  };

  const patch = useMemo<UpdateSubProjectBody>(() => {
    const next: UpdateSubProjectBody = {};
    if (draft.name !== (project.name ?? '')) next.name = draft.name.trim();
    if (draft.commune !== (project.commune ?? '')) next.commune = draft.commune.trim();

    // The input only sets/edits a non-empty externalId. Clearing is an explicit
    // action (the "Desvincular" button), never an empty text box.
    const nextExternalId = draft.externalId.trim();
    if (nextExternalId !== '' && nextExternalId !== (project.externalId ?? '')) {
      next.externalId = nextExternalId;
    }
    return next;
  }, [draft, project]);

  const isDirty = Object.keys(patch).length > 0;
  const externalIdChanged = 'externalId' in patch;

  const performSave = async (patchToSave: UpdateSubProjectBody) => {
    if (Object.keys(patchToSave).length === 0) return;
    setError(null);

    try {
      await updateSubProject({
        id: project.id,
        masterProjectId: project.masterProjectId,
        ...patchToSave,
      }).unwrap();
      setSavedAt(Date.now());
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar el proyecto.'));
      // A rejected externalId was never persisted, so the input would otherwise keep showing a value
      // the project does not have. Put it back to the stored one and let the operator retry.
      if (isExternalIdConflict(saveError) && 'externalId' in patchToSave) {
        setDraft((prev) => ({ ...prev, externalId: project.externalId ?? '' }));
      }
    } finally {
      setConfirmOpen(false);
      setPendingPatch(null);
    }
  };

  const handleSave = () => {
    if (!isDirty) return;
    if (externalIdChanged) {
      setPendingPatch(patch);
      setConfirmOpen(true);
      return;
    }
    void performSave(patch);
  };

  const handleUnlinkExternalId = () => {
    setPendingPatch({ ...patch, externalId: null });
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setPendingPatch(null);
  };

  return (
    <section className="space-y-4 rounded-md border border-border p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Datos del proyecto</h2>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && !isDirty && <span className="text-xs text-text-muted">Guardado</span>}
          <Button type="button" onClick={handleSave} disabled={!isDirty || isLoading}>
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`subproject-name-${project.id}`}>Nombre</Label>
          <Input
            id={`subproject-name-${project.id}`}
            value={draft.name}
            onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor={`subproject-commune-${project.id}`}>{communeLabel}</Label>
          <Input
            id={`subproject-commune-${project.id}`}
            value={draft.commune}
            placeholder="Ej: Las Condes"
            onChange={(event) => setDraft((prev) => ({ ...prev, commune: event.target.value }))}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor={`subproject-model-${project.id}`}>Modelo IA</Label>
          <Input
            id={`subproject-model-${project.id}`}
            value={project.openAIModel ?? 'Usa el modelo del proyecto maestro'}
            disabled
            className="opacity-60"
          />
          <p className="text-xs text-text-muted">
            Para cambiarlo, publica una versión del prompt con otro modelo y promuévela a production.
          </p>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`subproject-external-id-${project.id}`}>External ID (CRM)</Label>
        {showExternalIdInput ? (
          <>
            <div className="flex items-center gap-2">
              <Input
                id={`subproject-external-id-${project.id}`}
                value={draft.externalId}
                onChange={(event) => setDraft((prev) => ({ ...prev, externalId: event.target.value }))}
              />
              {isCreatingExternalId ? (
                <Button type="button" variant="ghost" size="sm" onClick={cancelCreateExternalId}>
                  Cancelar
                </Button>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={handleUnlinkExternalId} disabled={isLoading}>
                  Desvincular
                </Button>
              )}
            </div>
            <p className="text-xs text-text-muted">
              ID del proyecto en el CRM del cliente o en algún sistema externo a Lidz. Puede servir para sincronizar
              leads, stocks, etc.
            </p>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-muted">Sin definir</span>
            <Button type="button" variant="outline" size="sm" onClick={() => setIsCreatingExternalId(true)}>
              Configurar
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
        >
          <p className="flex-1">{error}</p>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Cerrar mensaje de error"
            onClick={() => setError(null)}
            className="-my-1 -mr-1 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <X aria-hidden />
          </Button>
        </div>
      )}

      <AlertDialog open={confirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modificar External ID</AlertDialogTitle>
            <AlertDialogDescription>
              Modificar el valor de ExternalId puede tener impacto en la sync con CRM y/o otros sistemas. Confirma el
              cambio solo si estás segura/o de lo que estás haciendo
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading} onClick={closeConfirm}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isLoading}
              className="gap-2"
              onClick={() => {
                if (pendingPatch) void performSave(pendingPatch);
              }}
            >
              {isLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default SubProjectIdentityCard;
